<script setup lang="ts">
  defineOptions({ name: 'EventsDirectorySection' })

  import { computed, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'
  import { useInfiniteScroll } from '@common/shared-ui-layer/composables/useInfiniteScroll'
  import GridLoadingIndicator from '@common/shared-ui-layer/components/GridLoadingIndicator.vue'

  import EventCard from '../molecules/EventCard.vue'
  import EventCardSkeleton from '../molecules/EventCardSkeleton.vue'
  import SectionEmptyState from '../molecules/SectionEmptyState.vue'
  import type { EventsHubCard, EventsHubListDensity, EventsHubResourceState } from '../../types/events-hub'

  const props = defineProps({
    state: {
      type: String as PropType<EventsHubResourceState>,
      required: true,
    },
    events: {
      type: Array as PropType<readonly EventsHubCard[]>,
      default: () => [],
    },
    hasNextPage: {
      type: Boolean,
      default: false,
    },
    isFetchingNextPage: {
      type: Boolean,
      default: false,
    },
    isSearchActive: {
      type: Boolean,
      default: false,
    },
    itemLimit: {
      type: Number,
      default: 12,
    },
    density: {
      type: String as PropType<EventsHubListDensity>,
      default: 'comfortable',
    },
    idPrefix: {
      type: String,
      default: 'events-directory',
    },
  })

  const emit = defineEmits<{
    openEvent: [eventId: string]
    loadMore: []
  }>()

  const { t } = useI18n()
  const SKELETON_CARD_COUNT = 3

  const gapClass = computed(() => {
    const map = { compact: 'gap-2', comfortable: 'gap-4', roomy: 'gap-6' } as const
    return map[props.density]
  })

  const emptyTitle = computed(() =>
    props.isSearchActive ? t('clientportal.events.emptyState.noSearchResultsTitle') : t('clientportal.events.emptyState.noEventsTitle')
  )
  const emptyDescription = computed(() =>
    props.isSearchActive
      ? t('clientportal.events.emptyState.noSearchResultsDescription')
      : t('clientportal.events.emptyState.noEventsDescription')
  )

  const { loadMoreTrigger } = useInfiniteScroll(() => emit('loadMore'), {
    enabled: () =>
      props.hasNextPage &&
      !props.isFetchingNextPage &&
      props.state !== 'loading' &&
      props.state !== 'error' &&
      (props.state === 'ready' || props.isSearchActive),
  })

  const handleOpenEvent = (eventId: string) => {
    emit('openEvent', eventId)
  }
</script>

<template>
  <section :id="createElementId(idPrefix, 'section', 'root')" class="flex w-full flex-col" :class="gapClass">
    <div
      v-if="state === 'loading'"
      :id="createElementId(idPrefix, 'section', 'loading-grid')"
      class="grid grid-cols-[repeat(auto-fill,minmax(min(100%,400px),400px))] gap-4"
      role="status"
      :aria-label="t('clientportal.events.loadingCardsAriaLabel')"
    >
      <EventCardSkeleton v-for="index in SKELETON_CARD_COUNT" :key="index" :id-prefix="idPrefix" :index="index" />
    </div>

    <SectionEmptyState
      v-else-if="state === 'error'"
      :id="createElementId(idPrefix, 'empty-state', 'error')"
      :title="t('clientportal.events.emptyState.loadErrorTitle')"
      :description="t('clientportal.events.emptyState.loadErrorDescription')"
    />

    <template v-else>
      <SectionEmptyState
        v-if="state === 'empty' || events.length === 0"
        :id="createElementId(idPrefix, 'empty-state', isSearchActive ? 'search' : 'empty')"
        :title="emptyTitle"
        :description="emptyDescription"
      />

      <div
        v-else
        :id="createElementId(idPrefix, 'section', 'content-grid')"
        class="grid grid-cols-[repeat(auto-fill,minmax(min(100%,400px),400px))] gap-4"
      >
        <EventCard
          v-for="(event, index) in events"
          :key="event.id"
          :event="event"
          :id-prefix="idPrefix"
          :priority="index === 0"
          @open-event="handleOpenEvent"
        />
      </div>

      <div v-if="hasNextPage || isFetchingNextPage || (isSearchActive && hasNextPage)" ref="loadMoreTrigger">
        <GridLoadingIndicator
          :is-fetching-next-page="isFetchingNextPage"
          :has-next-page="hasNextPage"
          :item-count="events.length"
          :min-items-for-end-message="itemLimit"
          :end-of-list-message="t('clientportal.events.directory.allLoaded')"
        />
      </div>
    </template>
  </section>
</template>
